import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Loader2, MailCheck, Sparkles } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Logo } from "@/components/Logo";

const field =
  "h-11 bg-zinc-900/60 border-zinc-800 text-zinc-100 placeholder:text-zinc-600 focus-visible:ring-indigo-500/40";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen nv-grid-bg flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-[420px] nv-rise">
        <div className="flex items-center justify-between gap-3 mb-10">
          <div className="flex items-center gap-2.5">
            <Logo size={30} withWordmark className="text-lg" testid="auth-logo" />
          </div>
          <ThemeToggle />
        </div>

        {sent ? (
          <div data-testid="forgot-sent" className="space-y-5">
            <div className="w-11 h-11 rounded-xl bg-indigo-500/15 border border-indigo-500/30 grid place-items-center">
              <MailCheck className="w-5 h-5 text-indigo-400" />
            </div>
            <h1 className="text-3xl font-extrabold tracking-tight">Check your inbox</h1>
            <p className="text-sm text-zinc-400 leading-relaxed">
              If an account exists for <span className="font-medium text-zinc-200 break-all">{email}</span>, a reset
              link is on its way. It works once and expires in 60 minutes.
            </p>
            <Link data-testid="forgot-back-to-login" to="/login" className="inline-block text-sm text-indigo-400 hover:text-indigo-300">
              Back to sign in
            </Link>
          </div>
        ) : (
          <>
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Forgot your password?</h1>
            <p className="text-sm text-zinc-500 mt-2 mb-8">
              Enter your work email and we'll send a link to choose a new one.
            </p>
            <form onSubmit={submit} className="space-y-3">
              <Input
                data-testid="forgot-email-input"
                className={field}
                type="email"
                required
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              {error && (
                <p data-testid="forgot-error" className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
                  {error}
                </p>
              )}
              <Button data-testid="forgot-submit-btn" type="submit" disabled={busy} className="w-full h-11 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Send reset link <ArrowRight className="w-4 h-4 ml-1.5" /></>}
              </Button>
            </form>
            <p className="text-sm text-zinc-500 mt-6">
              Remembered it?{" "}
              <Link data-testid="forgot-goto-login" to="/login" className="text-indigo-400 hover:text-indigo-300">
                Sign in
              </Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
